import { PLATFORM_LABELS, type Distribution, type Target } from '@shopx-distribution/shared';
import { Link } from 'react-router-dom';

import { PlatformIcon } from '@/components/PlatformIcon.js';
import { Card, StatusBadge } from '@/components/ui.js';
import { formatAccountLine, formatDateTime } from '@/lib/utils.js';

/**
 * Shared by the Admin Console history and the Experience page, which sit in
 * different colour schemes and link to different detail routes — hence
 * `linkBase` rather than a hardcoded path.
 *
 * The destination is resolved from the caller's target list instead of being
 * stored on the run, so passing deactivated targets in matters: a removed
 * target would otherwise leave old rows showing a platform with no account.
 */
export function DistributionList({
  distributions,
  targets,
  linkBase,
  showSource = true,
}: {
  distributions: Distribution[];
  targets: Target[];
  linkBase: string;
  showSource?: boolean;
}) {
  const byId = new Map(targets.map((target) => [target.id, target]));

  return (
    <Card className="overflow-hidden">
      <table className="w-full text-sm">
        <thead className="border-b border-line text-left text-xs uppercase text-muted">
          <tr>
            {showSource ? <th className="px-4 py-3 font-medium">Project</th> : null}
            <th className="px-4 py-3 font-medium">Destination</th>
            <th className="px-4 py-3 font-medium">Submitted</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {distributions.map((distribution) => {
            const target = byId.get(distribution.targetId);
            return (
              <tr key={distribution.id} className="border-b border-line-subtle last:border-0">
                {showSource ? (
                  <td className="px-4 py-3 font-medium">{distribution.sourceName}</td>
                ) : null}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <PlatformIcon platform={distribution.platform} />
                    <div>
                      <p className="text-body">{PLATFORM_LABELS[distribution.platform]}</p>
                      {target ? (
                        <p className="text-xs text-muted">
                          {formatAccountLine(target.name, target.externalTargetId)}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-body">{formatDateTime(distribution.createdAt)}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={distribution.status} />
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`${linkBase}/${distribution.id}`}
                    className="text-sm font-medium text-muted hover:text-ink hover:underline"
                  >
                    View
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
